import { Box, Flex, FlexProps, Text } from "@chakra-ui/react";
import { ChatLog } from "./components/ChatLog";
import { ChatSubmitForm } from "./components/ChatSubmitForm";
import { ChatHeader } from "./components/ChatHeader";
import { ChatEventCountdown } from "./components/ChatEventCountdown";
import { sqlDateToTimestamp } from "../../utils/time";
import { useAccountData } from "../../hooks/useAccountData";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { SealCheckThickIcon } from "../icons/SealCheckThickIcon";
import Autolink from "../navigation/AutoLink";

type ChatProps = {
  compact?: boolean;
  smallHeader?: boolean;
  title?: string;
  icon?: string | JSX.Element;
  button?: JSX.Element;
  eventStartTime?: string;
  guests?: string[];
  guestsIcon?: string;
  showHeader?: boolean;
  pinnedMessage?: string;
} & FlexProps;

export function Chat({
  smallHeader,
  compact,
  title,
  icon,
  button,
  eventStartTime = "0",
  guests,
  guestsIcon,
  showHeader = true,
  pinnedMessage,
  ...containerProps
}: ChatProps) {
  const accountData = useAccountData();
  const isSmallBreakpoint = useIsSmallBreakpoint();

  const eventStartTimestamp = sqlDateToTimestamp(eventStartTime);

  const showChatEarly =
    accountData?.user?.is_admin || guests?.includes(accountData?.user?.id + "");

  const chatEnabled =
    showChatEarly ||
    isNaN(eventStartTimestamp) ||
    eventStartTimestamp < Date.now();

  return (
    <Flex flexDirection="column" flexGrow={1} minWidth={0} {...containerProps}>
      {showHeader && (
        <ChatHeader
          compact={smallHeader}
          title={title}
          icon={icon}
          button={button}
        />
      )}
      {!chatEnabled ? (
        <ChatEventCountdown
          title={title}
          icon={icon}
          startTime={eventStartTimestamp ?? Infinity}
        />
      ) : null}
      {chatEnabled ? (
        <Flex flexGrow={1} flexDirection="column" minHeight={0}>
          {pinnedMessage && (
            <Box
              px={isSmallBreakpoint ? "16px" : "8px"}
              py="8px"
              border="1px solid"
              borderColor="transparent.white.10"
              mt="8px"
              borderRadius="16px"
            >
              <Flex gap="6px" alignItems="center" mb="4px">
                <Text fontWeight="600" fontSize="16px" color="blaze.blaze">
                  Admin
                </Text>
                <SealCheckThickIcon color="blaze.blaze" boxSize="16px" />
                <Text fontWeight="500" fontSize="12px" color="dune.700">
                  Pinned
                </Text>
              </Flex>
              <Text
                fontSize="15px"
                color="dune.dune"
                fontWeight="400"
                lineHeight="150%"
                whiteSpace="pre-wrap"
              >
                <Autolink text={pinnedMessage} />
              </Text>
            </Box>
          )}
          <ChatLog compact={compact} />
          <Box
            pb={isSmallBreakpoint ? "16px" : 0}
            px={isSmallBreakpoint ? "16px" : 0}
          >
            <ChatSubmitForm disabled={!chatEnabled} />
          </Box>
        </Flex>
      ) : null}
    </Flex>
  );
}
